import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { Card, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import Loading from 'egret/components/EgretLoadable/Loading'
import { CustomContentProps, SnackbarContent } from 'notistack'
import React, { ReactNode } from 'react'

export const snackbarClasses = (sfOauthConfig.isIcce === true)
  ? {
      success: { backgroundColor: '#569159' },
      error: { backgroundColor: '#C75656' },
      warning: { backgroundColor: '#DC8935' },
      info: { backgroundColor: '#338EC0' }
    }
  : {
      success: {
        backgroundColor: '#C5DBA6',
        color: '#17374F',
        padding: '32px 32px 32px 27px',
        width: '391px'
      },
      error: {
        backgroundColor: '#F4C1C1',
        padding: '32px 32px 32px 27px',
        width: '391px',
        color: '#17374F'
      },
      info: {
        backgroundColor: '#CAE5EF',
        padding: '32px 32px 32px 27px',
        width: '391px',
        color: '#17374F'
      },
      warning: {
        backgroundColor: '#F4D4B8',
        padding: '32px 32px 32px 27px',
        width: '391px',
        color: '#17374F'
      }
    }

const useStyles = makeStyles(snackbarClasses)

const variantIcons = {
  success: (
    <CheckCircleOutlineOutlinedIcon
      style={{ fontSize: 30, marginRight: 8, color: '#17374F' }}
    />
  ),
  error: (
    <CancelOutlinedIcon
      style={{ fontSize: 30, marginRight: 8, color: '#17374F' }}
    />
  ),
  warning: (
    <WarningAmberOutlinedIcon
      style={{ fontSize: 30, marginRight: 8, color: '#17374F' }}
    />
  ),
  info: (
    <InfoOutlinedIcon
      style={{ fontSize: 30, marginRight: 8, color: '#17374F' }}
    />
  )
}

interface CustomSnackbarProps extends Partial<CustomContentProps> {
  id?: string
  message?: ReactNode
  variant: 'success' | 'error' | 'warning' | 'info'
  icon?: ReactNode
}

/**
 * A reusable component that displays a Snackbar with a custom message and variant. It allows
 * displaying messages with different types of alerts (success, error, warning, info)
 * with corresponding icons.
 *
 * @component
 *
 * @param {Object} props - Component props.
 * @param {string} [props.id] - The unique identifier for the Snackbar.
 * @param {string} [props.message='Default message'] - The message to be displayed inside the Snackbar.
 * @param {'success' | 'error' | 'warning' | 'info'} props.variant - The variant of the Snackbar, determines
 * its style and icon.
 * @param {CustomContentProps} props.rest - Any additional props passed to the SnackbarContent component.
 *
 * @returns {JSX.Element} The rendered CustomSnackbar component.
 */
export const CustomSnackbar = React.forwardRef<
HTMLDivElement,
CustomSnackbarProps
>(({ id, message = 'Default message', variant, icon = null, ...rest }, ref) => {
  const classes = useStyles()
  const bgColor = snackbarClasses[variant]?.backgroundColor

  return (
    <SnackbarContent
      {...rest}
      ref={ref}
      role='alert'
      className={classes[variant]}
      style={{
        marginLeft: '30px',
        marginBottom: '30px',
        padding: '32px 32px 32px 27px',
        width: '391px',
        color: '#17374F',
        borderLeft: '10px solid #17374F',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        backgroundColor: (bgColor !== undefined && bgColor !== null && bgColor !== '')
          ? bgColor
          : 'transparent'
      }}
    >
      {(icon != null) || variantIcons[variant]}
      <Typography
        style={{
          color: '#17374F',
          fontFamily: 'Open Sans',
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '18px',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          width: '280px',
          maxWidth: '100%'
        }}
      >
        {message}
      </Typography>
    </SnackbarContent>
  )
})

/**
 * Displays a loading indicator with a message.It uses the CustomSnackbar component
 * with the 'info' variant and replaces the icon with the loading spinner.
 *
 * @component
 *
 * @param {ReactNode} message - The message to be displayed inside the Snackbar while loading.
 *
 * @returns {JSX.Element} The rendered ProgressSnackbar component, which includes a loading spinner and the provided message.
 */
export default function ProgressSnackbar (message: ReactNode): JSX.Element {
  return (
    <Card
      style={{
        marginLeft: '30px',
        marginBottom: '30px',
        padding: '32px 32px 32px 27px',
        width: '391px',
        color: '#17374F',
        borderLeft: '10px solid #17374F',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        backgroundColor: snackbarClasses.info.backgroundColor
      }}
    >
      <Loading
        style={{ marginLeft: 57, marginRight: 8, marginTop: 20 }}
        size={40}
      />
      <Typography
        style={{
          color: '#17374F',
          fontFamily: 'Open Sans',
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '18px',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          width: '280px',
          maxWidth: '100%',
          marginLeft: '40px'
        }}
      >
        {message}
      </Typography>
    </Card>
  )
}
